/*
 *  -----------------------------------------------------------------------
 *    <copyright file="delete-product-dialog.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Button from 'views/components/core/buttons';
import { useBoolean, useTranslation } from 'hooks';
import NotificationService from 'utils/notification';
import { ASSIGN_PRODUCTS, GET_STUDY } from 'queries/studies';

import 'views/components/admin/studies/admin-study.scss';
import { OrganisationProductFields, StudyFields } from 'queries/generated/fragments.generated';
import { CustomDialog, DialogTitle, DialogContent, DialogActions } from 'views/components/core/dialog';

const DeleteProductDialog = ({
    open,
    onClose,
    currentProduct,
    study,
}: {
    open: boolean;
    onClose: Function;
    currentProduct: OrganisationProductFields;
    study: StudyFields;
}) => {
    const { t } = useTranslation();
    const saving = useBoolean(false);
    const [assignProducts] = useMutation(ASSIGN_PRODUCTS);

    const close = (action?: any) => {
        onClose(action);
    };

    const errorRequest = () => {
        NotificationService.showNotification(t('errorMessage'));
        saving.setFalse();
    };

    const successRequest = (closeModal: boolean) => {
        saving.setFalse();
        NotificationService.showNotification(
            t('admin:productSuccessfullyUnassigned', {
                studyId: study.recordId,
                id: currentProduct?.recordId ?? currentProduct?.id,
            }),
        );
        if (closeModal) onClose();
    };

    const onDeleteProduct = (closeModal?: boolean) => {
        const productIndex = study.productsIds.findIndex((productId: any) => productId === currentProduct.id);
        const studyProductIds = Object.assign([], study.productsIds);
        studyProductIds.splice(productIndex, 1);

        saving.setTrue();

        assignProducts({
            variables: { studyId: study.id, reason: '', studyProductIds },
            refetchQueries: [{ query: GET_STUDY, variables: { id: study.id } }],
        })
            .then(() => {
                successRequest(closeModal);
            })
            .catch(() => {
                errorRequest();
            });
    };

    return (
        <CustomDialog onClose={close} fullWidth scroll="paper" maxWidth="md" open={open} className="custom-width-popup">
            <DialogTitle id="delete-product-modal-title">
                {t('admin:unassignProduct')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={close}
                    data-testid="close-dialog"
                />
            </DialogTitle>

            <DialogContent className="delete-product-dialog-content">
                <>
                    <div>
                        <span className="confirm-text pb">{t('admin:areYouSureYouWantToDeleteThisProduct')}</span>
                    </div>
                </>
            </DialogContent>

            <DialogActions className="delete-product dialog-actions flex-end">
                <Button
                    disabled={saving.value}
                    id="delete-product-save"
                    text="unassign"
                    secondary
                    onClick={onDeleteProduct}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default DeleteProductDialog;
